import { apiClient } from '@/core/api-client/client';

export type NotificationChannel = 'email' | 'sms' | 'push';
export type NotificationAudience =
  | 'teachers'
  | 'counselors'
  | 'school_admins'
  | 'guardians'
  | 'students'
  | 'all_staff';
export type TemplateCategory = 'manual' | 'automation';

export interface Notification {
  id: string;
  channel: string;
  template_key: string;
  status: string;
  sent_at: string | null;
  read_at?: string | null;
  created_at?: string | null;
  student_id?: string | null;
  recipient_user_id?: string | null;
  recipient_name?: string | null;
  student_name?: string | null;
  audience?: string | null;
  form_level?: string | null;
  preview?: string | null;
  is_read?: boolean;
}

export interface NotificationCreate {
  recipient_user_id?: string | null;
  student_id?: string | null;
  channel: NotificationChannel;
  template_key: string;
  payload?: Record<string, unknown>;
}

export interface NotificationSettings {
  school_id: string;
  default_channel: NotificationChannel;
  email_enabled: boolean;
  sms_enabled: boolean;
  push_enabled: boolean;
  plan_channels?: {
    email?: boolean;
    sms?: boolean;
    push?: boolean;
  };
  low_attendance_notifications_enabled: boolean;
  low_attendance_channel: NotificationChannel;
  report_card_channel: NotificationChannel;
  scholarship_deadline_channel: NotificationChannel;
  weekly_digest_enabled: boolean;
  weekly_digest_channel: NotificationChannel;
  manual_send_default_template_key: string;
  birthday_enabled: boolean;
  birthday_channel: NotificationChannel;
  birthday_template_key: string;
  birthday_send_time: string;
  birthday_audience: 'guardians' | 'students' | 'teachers';
  updated_at: string;
}

export type NotificationSettingsUpdate = Partial<{
  default_channel: NotificationChannel;
  email_enabled: boolean;
  sms_enabled: boolean;
  push_enabled: boolean;
  low_attendance_notifications_enabled: boolean;
  low_attendance_channel: NotificationChannel;
  report_card_channel: NotificationChannel;
  scholarship_deadline_channel: NotificationChannel;
  weekly_digest_enabled: boolean;
  weekly_digest_channel: NotificationChannel;
  manual_send_default_template_key: string;
  birthday_enabled: boolean;
  birthday_channel: NotificationChannel;
  birthday_template_key: string;
  birthday_send_time: string;
  birthday_audience: 'guardians' | 'students' | 'teachers';
}>;

export interface NotificationTemplate {
  id: string;
  school_id: string;
  key: string;
  name: string;
  category: TemplateCategory;
  subject: string | null;
  body: string;
  allowed_channels: NotificationChannel[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export type NotificationTemplateCreate = {
  key: string;
  name: string;
  category?: TemplateCategory;
  subject?: string | null;
  body?: string;
  allowed_channels?: NotificationChannel[];
  is_active?: boolean;
};

export type NotificationTemplateUpdate = Partial<{
  name: string;
  category: TemplateCategory;
  subject: string | null;
  body: string;
  allowed_channels: NotificationChannel[];
  is_active: boolean;
}>;

export interface NotificationBroadcastCreate {
  channel: NotificationChannel;
  template_id?: string;
  template_key?: string;
  audience: NotificationAudience;
  form_level?: string | null;
  education_level?: string | null;
  recipient_user_id?: string | null;
  extra_context?: Record<string, unknown>;
}

export interface NotificationBroadcastResult {
  queued: number;
  skipped: number;
  template_key: string;
}

export async function listNotifications(): Promise<Notification[]> {
  const { data } = await apiClient.get<Notification[]>('/notifications');
  return data;
}

export async function markNotificationRead(id: string): Promise<Notification> {
  const { data } = await apiClient.post<Notification>(`/notifications/${id}/read`);
  return data;
}

export function isNotificationUnread(n: Notification): boolean {
  if (typeof n.is_read === 'boolean') return !n.is_read;
  return !n.read_at;
}

export async function sendNotification(body: NotificationCreate): Promise<Notification> {
  const { data } = await apiClient.post<Notification>('/notifications', body);
  return data;
}

export async function broadcastNotification(
  body: NotificationBroadcastCreate,
): Promise<NotificationBroadcastResult> {
  const { data } = await apiClient.post<NotificationBroadcastResult>(
    '/notifications/broadcast',
    body,
  );
  return data;
}

export async function getNotificationSettings(): Promise<NotificationSettings> {
  const { data } = await apiClient.get<NotificationSettings>('/notifications/settings');
  return data;
}

export async function updateNotificationSettings(
  body: NotificationSettingsUpdate,
): Promise<NotificationSettings> {
  const { data } = await apiClient.patch<NotificationSettings>('/notifications/settings', body);
  return data;
}

export async function listNotificationTemplates(
  activeOnly = false,
): Promise<NotificationTemplate[]> {
  const { data } = await apiClient.get<NotificationTemplate[]>('/notifications/templates', {
    params: { active_only: activeOnly },
  });
  return data;
}

export async function createNotificationTemplate(
  body: NotificationTemplateCreate,
): Promise<NotificationTemplate> {
  const { data } = await apiClient.post<NotificationTemplate>('/notifications/templates', body);
  return data;
}

export async function updateNotificationTemplate(
  id: string,
  body: NotificationTemplateUpdate,
): Promise<NotificationTemplate> {
  const { data } = await apiClient.patch<NotificationTemplate>(
    `/notifications/templates/${id}`,
    body,
  );
  return data;
}

export async function deleteNotificationTemplate(id: string): Promise<void> {
  await apiClient.delete(`/notifications/templates/${id}`);
}
